import {
	AbstractControl,
	FormArray,
	ValidationErrors,
	ValidatorFn
} from "@angular/forms";

function createPasswordStrengthValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const value = control.value as string;

		const hasUpperCase = /[A-Z]+/.test(value);

		const passwordValid = hasUpperCase;

		return !passwordValid ? { passwordStrength: true } : null;
	};
}

function passwordMatchValidator(
	{ passwordField, confirmPasswordField } = {
		passwordField: "",
		confirmPasswordField: ""
	}
): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control) {
			return null;
		}

		const passwordValue = control.get(passwordField)?.value as string;
		const confirmPasswordValue = control.get(confirmPasswordField)
			?.value as string;

		return passwordValue !== confirmPasswordValue
			? { passwordDoesNotMatch: true }
			: null;
	};
}

function invalidCommissionValue(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}
		const isInvalidValue = control.value > 999.99;

		return isInvalidValue ? { invalidComissionValue: true } : null;
	};
}

function invalidCurrencyFormatValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const currencyFormatRegexp = new RegExp("^-?[0-9]\\d*(\\.\\d{1,2})?$");
		const value = control.value as string;
		const isInvalidCurrencyFormat = !currencyFormatRegexp.test(value);

		return isInvalidCurrencyFormat ? { currencyInvalidFormat: true } : null;
	};
}

function invalidIntegerNumberFormatValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const integerNumberFormatRegexp = new RegExp("^[0-9]*$");
		const value = control.value as string;
		const isInvalidIntegerNumberFormat =
			!integerNumberFormatRegexp.test(value);

		return isInvalidIntegerNumberFormat
			? { integerNumberInvalidFormat: true }
			: null;
	};
}

function minSelectedCheckboxesDetails(
	min = 1,
	onlyValidateWithDirtyControl = false
): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const formArrayControls = (control as FormArray).controls;

		if (!formArrayControls) {
			return null;
		}

		const selectedElements = formArrayControls
			.map((control) => control.value as boolean)
			.filter(Boolean).length;

		if (onlyValidateWithDirtyControl) {
			if (control.dirty) {
				return selectedElements >= min ? null : { required: true };
			}
			return null;
		}

		return selectedElements >= min ? null : { required: true };
	};
}

function minInstallments(min = 1): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value && control.value !== 0) {
			return null;
		}

		const value = control.value as number;

		const isInvalid = value < min;

		return isInvalid
			? {
					minInstallments: {
						minInstallment: min,
						actual: +control.value
					}
				}
			: null;
	};
}

function maxInstallments(max = 1): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}

		const value = control.value as number;

		const isInvalid = value > max;

		return isInvalid
			? {
					maxInstallments: {
						maxInstallment: max,
						actual: +control.value
					}
				}
			: null;
	};
}

function dateTimeInterdependenceValidator(
	dateFieldName: string,
	timeFieldName: string
): ValidatorFn {
	return (formGroup: AbstractControl): ValidationErrors | null => {
		if (!formGroup) {
			return null;
		}
		const dateControl = formGroup.get(dateFieldName);
		const timeControl = formGroup.get(timeFieldName);
		if (!dateControl || !timeControl) {
			return null;
		}

		const rawDateValue = ((dateControl.value as string) || "").trim();
		const rawTimeValue = ((timeControl.value as string) || "").trim();

		if (!rawDateValue && !rawTimeValue) {
			return null;
		}
		if (rawDateValue && !rawTimeValue) {
			return { missingTime: true };
		}
		if (!rawDateValue && rawTimeValue) {
			return { missingDate: true };
		}
		return null;
	};
}

export {
	createPasswordStrengthValidator,
	invalidCurrencyFormatValidator,
	invalidIntegerNumberFormatValidator,
	passwordMatchValidator,
	invalidCommissionValue,
	minSelectedCheckboxesDetails,
	minInstallments,
	maxInstallments,
	dateTimeInterdependenceValidator
};
